import { useEffect, useId, useRef, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { cn, AppLogo, ChevronDownIcon } from '../ui';
import { useIdentity } from '../providers/IdentityProvider';
import { NAV_GROUPS, type NavItem } from './navModel';

// A pathname "belongs" to an item if it matches exactly, or — for non-`end` items — sits
// nested under it (e.g. a future `/admin/users/123` detail route still belongs to the
// `/admin/users` item). `end` items (index routes like `/admin`, `/commission`) only ever
// claim their own exact path, mirroring NavLink's own `end` matching semantics.
function itemMatches(item: NavItem, pathname: string): boolean {
  if (pathname === item.to) return true;
  if (item.end === true) return false;
  return pathname.startsWith(item.to.endsWith('/') ? item.to : `${item.to}/`);
}

function findActiveGroupTitle(
  pathname: string,
  isVisible: (item: NavItem) => boolean,
): string | null {
  for (const group of NAV_GROUPS) {
    const items = group.items.filter(isVisible);
    if (items.length > 0 && items.some((item) => itemMatches(item, pathname))) {
      return group.title;
    }
  }
  return null;
}

export function Sidebar({
  open,
  navId,
  onClose,
}: {
  open: boolean;
  navId: string;
  onClose: () => void;
}): JSX.Element {
  const { identity } = useIdentity();
  const roles = identity?.roles ?? [];
  const visible = (item: NavItem): boolean =>
    item.roles === undefined || item.roles.some((r) => roles.includes(r));

  const navRef = useRef<HTMLElement>(null);
  const panelIdBase = useId();
  const { pathname } = useLocation();
  // Stable primitive proxy for "role-based visibility could have changed" — identity loads
  // asynchronously, so `roles` starts empty and populates a render or two after mount.
  const rolesKey = roles.join(',');

  // Single-open accordion, synced to the route: whenever the pathname changes (including
  // the initial mount) or the visible role set resolves/changes, the group containing the
  // current route becomes the sole open group. Between those events, manual toggle clicks
  // (open/close) are left untouched, so this never fights a manual close of the active group.
  const [openGroup, setOpenGroup] = useState<string | null>(() => findActiveGroupTitle(pathname, visible));
  useEffect(() => {
    setOpenGroup(findActiveGroupTitle(pathname, visible));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, rolesKey]);

  const toggleGroup = (title: string): void => {
    setOpenGroup((cur) => (cur === title ? null : title));
  };

  // Below md, the open panel is an off-canvas overlay — Escape dismisses it, matching the
  // existing Modal/Drawer convention (md+ ignores this: `open` never controls visibility there).
  useEffect(() => {
    if (!open) {
      return;
    }
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  // Moves focus onto the first enabled, currently-visible link the instant the panel opens
  // — keyboard/AT users land inside the newly-shown navigation rather than on the
  // now-hidden trigger. Full Tab trapping is out of scope for this correction (shared
  // Modal/Drawer focus-trap work). If every group is collapsed (no route-matching group,
  // manually closed), no link is visible — fall back to the first group toggle instead.
  useEffect(() => {
    if (!open) {
      return;
    }
    const first = navRef.current?.querySelector<HTMLAnchorElement>(
      'div:not([hidden]) > a:not([aria-disabled="true"])',
    );
    if (first) {
      first.focus();
      return;
    }
    navRef.current?.querySelector<HTMLButtonElement>('button[aria-expanded]')?.focus();
  }, [open]);

  return (
    <>
      {open && (
        <div
          className="fixed inset-0 z-modal animate-fade-in bg-black/40 md:hidden"
          onClick={onClose}
          aria-hidden
        />
      )}
      <aside
        id={navId}
        className={cn(
          'w-60 shrink-0 border-e border-border bg-surface',
          // Mobile: off-canvas overlay, out of flow — never pushes/compresses <main>.
          'fixed inset-y-0 start-0 z-modal shadow-e3',
          open ? 'block' : 'hidden',
          // Every breakpoint: the panel scrolls its own nav vertically. Desktop used to set
          // `md:overflow-visible` — an expanded group (ოპერაციები) is taller than the viewport,
          // so the nav spilled past the aside's box (outside its background/border) and made the
          // whole document scroll, dragging the TopBar and page content with it.
          'overflow-y-auto',
          // Desktop: in-flow, always-visible column, stretched to the shell's full height.
          'md:static md:inset-auto md:z-auto md:block md:shadow-none',
        )}
      >
        {/* min-h, not h- (Slice C2 h-[N] audit): the sidebar's fixed w-60 width leaves ~208px for
            this header after padding — the same fixed-height/wrappable-content defect class
            already found in KpiCard/TopBar. A minimum lets the header grow instead of
            clipping/overlapping the nav below it. Decorative only — not a link, no route change. */}
        <div className="flex min-h-14 items-center overflow-hidden border-b border-border px-4">
          <AppLogo size="sm" className="min-w-0" />
        </div>
        <nav ref={navRef} className="flex flex-col gap-5 p-3 text-sm" aria-label="მთავარი ნავიგაცია">
          {NAV_GROUPS.map((group, groupIndex) => {
            const items = group.items.filter(visible);
            if (items.length === 0) return null;
            const isOpen = openGroup === group.title;
            const panelId = `${panelIdBase}-group-${groupIndex}`;
            return (
            <div key={group.title} className="flex flex-col">
              <button
                type="button"
                aria-expanded={isOpen}
                aria-controls={panelId}
                onClick={() => toggleGroup(group.title)}
                className={cn(
                  'flex min-h-11 w-full items-center justify-between gap-2 rounded-token px-3 py-2 text-sm font-semibold uppercase tracking-wide text-fg transition-colors hover:bg-surface-muted',
                  isOpen && 'bg-primary/5',
                )}
              >
                <span>{group.title}</span>
                <ChevronDownIcon
                  className={cn('h-4 w-4 shrink-0 transition-transform duration-fast ease-standard', isOpen && 'rotate-180')}
                />
              </button>
              {/* `flex` must not coexist with `hidden` — a `display` utility class beats the
                  native [hidden] UA default (equal specificity, author wins), so the panel
                  would stay visually visible despite hidden=true. Only add it while open. */}
              <div id={panelId} hidden={!isOpen} className={cn('flex-col gap-1 pt-1', isOpen && 'flex')}>
                {items.map((item) => {
                  const Icon = item.icon;
                  return (
                    <NavLink
                      key={item.to}
                      to={item.to}
                      end={item.end ?? false}
                      onClick={onClose}
                      className={({ isActive }) =>
                        cn(
                          'flex items-center gap-2 rounded-token px-3 py-2 transition-colors',
                          isActive ? 'bg-surface-muted font-medium text-fg' : 'text-fg-muted hover:bg-surface-muted',
                        )
                      }
                    >
                      {/* currentColor stroke (icon-foundation contract) inherits this link's own
                          active/inactive text color automatically — no separate icon color class. */}
                      <Icon className="h-4 w-4 shrink-0" />
                      {item.label}
                    </NavLink>
                  );
                })}
              </div>
            </div>
            );
          })}
        </nav>
      </aside>
    </>
  );
}
